import React from 'react';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { X } from '@phosphor-icons/react';

import { cn } from '../../lib/utils';
import type { FileAttachment, FileAttachmentBlock } from './types';

// ---- Lightbox ----

interface LightboxProps {
  src: string;
  alt?: string;
  open: boolean;
  onClose: () => void;
}

function Lightbox({ src, alt, open, onClose }: LightboxProps) {
  return (
    <DialogPrimitive.Root
      open={open}
      onOpenChange={(o) => {
        if (!o) onClose();
      }}
    >
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="fixed inset-0 z-50 bg-black/80 cursor-zoom-out data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0" />
        <DialogPrimitive.Content
          className="fixed left-1/2 top-1/2 z-50 max-w-[calc(100vw-4rem)] max-h-[calc(100vh-4rem)] outline-none data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95"
          style={{ translate: '-50% -50%' }}
          aria-describedby={undefined}
          onPointerDownOutside={onClose}
        >
          <DialogPrimitive.Title className="sr-only">{alt ?? 'Image preview'}</DialogPrimitive.Title>
          <img
            src={src}
            alt={alt ?? 'Attachment'}
            className="max-w-full max-h-full object-contain rounded-lg ring-1 ring-white/10"
          />
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}

// ---- Single thumbnail ----

interface ThumbnailProps {
  file?: File;
  url?: string;
  alt?: string;
  loading?: boolean;
  onRemove?: () => void;
}

function Thumbnail({ file, url, alt, loading, onRemove }: ThumbnailProps) {
  const [objectUrl, setObjectUrl] = React.useState<string | undefined>();
  const [lightboxOpen, setLightboxOpen] = React.useState(false);

  React.useEffect(() => {
    if (!file) return;
    const obj = URL.createObjectURL(file);
    setObjectUrl(obj);

    return () => URL.revokeObjectURL(obj);
  }, [file]);

  const src = url ?? objectUrl;
  if (!src) return null;

  return (
    <>
      <div className="relative group size-16 rounded-lg overflow-hidden flex-shrink-0 ring-1 ring-black/10 dark:ring-white/10">
        <button
          type="button"
          className="w-full h-full cursor-zoom-in"
          onClick={() => setLightboxOpen(true)}
          disabled={loading}
        >
          <img src={src} alt={alt ?? 'Attachment'} className="w-full h-full object-cover" />
        </button>
        {loading && (
          <div className="absolute inset-0 flex items-center justify-center bg-black/40 pointer-events-none">
            <div className="animate-spin rounded-full size-4 border-[2.5px] border-black/30 border-t-white" />
          </div>
        )}
        {onRemove && (
          <button
            type="button"
            onClick={onRemove}
            className="absolute top-0.5 right-0.5 p-0.5 rounded-full opacity-0 group-hover:opacity-100 transition-opacity bg-black/50 hover:bg-black/70"
          >
            <X className="size-2.5 text-white" aria-label="Remove" />
          </button>
        )}
      </div>
      <Lightbox src={src} alt={alt} open={lightboxOpen} onClose={() => setLightboxOpen(false)} />
    </>
  );
}

// ---- Input (pending attachments) ----

interface InputImageThumbnailsProps {
  attachments: FileAttachment[];
  onRemove?: (attachment: FileAttachment) => void;
  className?: string;
}

export function InputImageThumbnails({ attachments, onRemove, className }: InputImageThumbnailsProps) {
  const images = attachments.filter((a) => a.file.mimeType.startsWith('image/'));
  if (images.length === 0) return null;

  return (
    <div className={cn('flex flex-wrap gap-2', className)}>
      {images.map((a) => (
        <Thumbnail
          key={a.id}
          file={a.raw}
          url={a.file.url}
          alt={a.file.name}
          loading={a.loading}
          onRemove={onRemove ? () => onRemove(a) : undefined}
        />
      ))}
    </div>
  );
}

// ---- Message (sent blocks) ----

interface MessageImageThumbnailsProps {
  blocks: FileAttachmentBlock[];
  className?: string;
}

export function MessageImageThumbnails({ blocks, className }: MessageImageThumbnailsProps) {
  const images = blocks.filter((b) => b.mimeType.startsWith('image/'));
  if (images.length === 0) return null;

  return (
    <div className={cn('flex flex-wrap gap-2', className)}>
      {images.map((b) => (
        <Thumbnail key={b.attachmentId} url={b.url} alt={b.fileName} />
      ))}
    </div>
  );
}
